<template>
  <div>
    <div class="row mb-3">
      <div class="col-md-3">
        <div class="form-group">
          <label for="type-select">Type</label>
          <select v-model="filters.type" class="form-select" @change="refreshEvents">
            <option value="">All</option>
            <option v-for="(name, id) in types" :key="id" :value="id">{{ name }}</option> 
          </select>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="staff-select">Staff</label>
          <select v-model="filters.staff" class="form-select" @change="refreshEvents">
            <option value="">All</option>
            <option v-for="user in staffUsers" :key="user.id" :value="user.id">{{ user.full_name }}</option> 
          </select>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="status-select">Status</label>
          <select v-model="filters.status" class="form-select" @change="refreshEvents">
            <option value="">All</option>
            <option v-for="(name, id) in statuses" :key="id" :value="id">{{ name }}</option> 
          </select>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="location-select">Location</label>
          <select v-model="filters.location" class="form-select" @change="refreshEvents">
            <option value="">All</option>
            <option v-for="(name, id) in locations" :key="id" :value="id">{{ name }}</option> 
          </select>
        </div>
      </div>
    </div>

    <div class="d-flex flex-wrap align-items-center gap-3 mb-3 p-2 bg-light rounded border">
      <span class="fw-bold small text-muted me-1">Legend:</span>
      <span class="badge d-inline-flex align-items-center gap-1 text-dark fw-normal bg-white border">
        <i class="bi bi-circle-fill" style="color: var(--visit-core-color);"></i> Core Visit
      </span>
      <span class="badge d-inline-flex align-items-center gap-1 text-dark fw-normal bg-white border">
        <i class="bi bi-circle-fill" style="color: var(--visit-short-color);"></i> Short Visit
      </span>
      <span class="badge d-inline-flex align-items-center gap-1 text-dark fw-normal bg-white border">
        <i class="bi bi-circle-fill" style="color: var(--visit-adhoc-color);"></i> Ad-Hoc
      </span>
      <span class="badge d-inline-flex align-items-center gap-1 text-dark fw-normal bg-white border">
        <i class="bi bi-circle-fill" style="color: var(--visit-preg-color);"></i> Pregnancy
      </span>
      <span class="badge d-inline-flex align-items-center gap-1 text-dark fw-normal bg-white border">
        <i class="bi bi-circle-fill" style="color: var(--visit-post-color);"></i> Post-Partum
      </span>
    </div>

    <FullCalendar 
      ref="calendarRef"
      :options="calendarOptions"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import bootstrap5Plugin from '@fullcalendar/bootstrap5'
import 'bootstrap-icons/font/bootstrap-icons.css'

const props = defineProps({
  appointments: Object,
  staffUsers: Object,
  locations: Object,
  types: Object,
  statuses: Object
})

const calendarRef = ref(null)

const filters = reactive({
  type: '',
  staff: '',
  status: '',
  location: ''
})

const typeToColor = computed(() => {
  const styles = getComputedStyle(document.documentElement)
  
  return {
    "Core Visit": styles.getPropertyValue('--visit-core-color').trim() || '#198754',    
    "Short Visit": styles.getPropertyValue('--visit-short-color').trim() || '#0dcaf0',   
    "Ad-Hoc": styles.getPropertyValue('--visit-adhoc-color').trim() || '#d97706',      
    "Pregnancy": styles.getPropertyValue('--visit-preg-color').trim() || '#2563eb',     
    "Post-Partum": styles.getPropertyValue('--visit-post-color').trim() || '#e11d48',   
  }
})

const buildTitle = (appt) => {
  let title = appt.appointment_location ? appt.appointment_location.name + ' - ' : ''
  title += appt.attendee.full_name + ' (' + appt.attendee.id + ') ' + appt.attendee.star_id + "\n"
  title += appt.appointment_type.name + ' (' + appt.id + ')' + "\n"
  title += 'Interview: ' + appt.staff.full_name + "\n"
  title += appt.clinician_user_id ? 'Clinician: ' + appt.clinician.full_name + "\n" : ''
  title += appt.transportation ? 'Transportation: ' + appt.transportation + "\n" : ''
  return title
}

const transformAppointments = (data) => {
  const typeName = data.appointment_type.name
  return {
    id: data.id,
    title: buildTitle(data),
    start: data.serialized_starts_at,
    color: (data.appointment_status_id == 1) ? (typeToColor.value[typeName] || '#6c757d') : '#aaa',
    url: '/appointments/' + data.id,
    extendedProps: {
      location_id: data.appointment_location_id,
      type_id: data.appointment_type_id,
      status_id: data.appointment_status_id,
      staff_user_id: data.staff_user_id
    }
  }
}

const calendarOptions = reactive({
  plugins: [dayGridPlugin, bootstrap5Plugin, timeGridPlugin],
  initialView: 'timeGridDay',
  themeSystem: 'bootstrap5',
  headerToolbar: {
    left: 'prev,next today',
    center: 'title',
    right: 'dayGridMonth,timeGridWeek,timeGridDay'
  },
  nowIndicator: true,
  slotDuration: '00:30:00',
  defaultTimedEventDuration: '02:00:00',
  navLinks: true,
  eventDisplay: 'block',
  eventDataTransform: transformAppointments,
  eventSources: {
    events: props.appointments
  }
})

const filterEvents = () => {
  if (!calendarRef.value) return
  const calendarApi = calendarRef.value.getApi()
  const events = calendarApi.getEvents()

  for (const event of events) {
    event.setProp('display', 'auto')
    
    for (const filterKey in filters) {
      const val = filters[filterKey]
      if (val !== '') {
        switch (filterKey) {
          case 'staff':
            if (event.extendedProps.staff_user_id != val) event.setProp('display', 'none')
            break
          case 'location':
            if (event.extendedProps.location_id != val) event.setProp('display', 'none')
            break
          case 'type':
            if (event.extendedProps.type_id != val) event.setProp('display', 'none')
            break
          case 'status':
            if (event.extendedProps.status_id != val) event.setProp('display', 'none')
            break
        }
      }
    }
  }
}

const refreshEvents = () => {
  filterEvents()
}
</script>